<template>
  <div class="app-member-reviews-list">
    <AppReviewableListItem
      v-for="review in reviewsWithChildren"
      :key="review.id"
      :review="review"
      class="review-list-item"
      @likes-changed="$emit('likes-changed')"
    >
      <template v-if="!review.children.length && review.user.id !== me.id" #actions>
        <div>
          <button type="button" class="reply" @click="replyingReviewId = String(review.id)">Reply</button>
        </div>
      </template>

      <button
        v-if="review.children.length"
        class="replies-action d-block d-md-none"
        type="button"
        @click="showReply(review.id)"
      >
        View reply
      </button>

      <Transition name="expand" @after-enter="afterEnter" @enter="enter" @leave="leave">
        <div v-if="replyingReviewId === review.id">
          <form class="text-area" @submit.prevent="saveReplay(review)">
            <textarea v-model.trim="replyText" required placeholder="Start typing..." />
            <button class="save" type="submit">Send</button>
            <button class="close-btn" type="button" @click="close">
              <TheCrossIcon />
            </button>
          </form>
        </div>
      </Transition>

      <AppReviewableListItem
        v-for="child in review.children"
        :key="child.id"
        class="child"
        :class="{ 'child--show': repliedReviewId === review.id }"
        :review="child"
        @likes-changed="$emit('likes-changed')"
      />
    </AppReviewableListItem>
  </div>
</template>

<script>
import meQuery from '@/graphql/me/Me.query.gql';
import replyMemberReviewMutation from '@/graphql/memberReview/ReplyMemberReview.mutation.gql';
import AppReviewableListItem from '@/components/AppReviewableListItem.vue';
import TheCrossIcon from '@/components/icons/TheCrossIcon.vue';

export default {
  // This component is NOT used in the project. It was created to keep the REPLY feature that is not used frequency (and is not exist in the mobile app).
  // So, maybe it will be removed in the future.
  name: 'AppMemberReviewsListWithReply',
  components: { AppReviewableListItem, TheCrossIcon },
  props: {
    reviews: {
      type: Array,
      default: () => [],
    },
  },
  apollo: {
    me: {
      query: meQuery,
    },
  },
  data() {
    return {
      me: {},
      replyText: '',
      replyProcessing: false,
      replyingReviewId: null,
      repliedReviewId: null,
    };
  },
  computed: {
    reviewsWithChildren() {
      return this.reviews
        .filter((review) => review.children)
        .map((review) => ({
          ...review,
          createdAtForHuman: this.$moment(review.created_at).fromNow(),
          children: review.children.map((child) => ({
            ...child,
            createdAtForHuman: this.$moment(child.created_at).fromNow(),
          })),
        }));
    },
  },
  methods: {
    enter(element) {
      element.style.width = getComputedStyle(element).width;
      element.style.position = 'absolute';
      element.style.visibility = 'hidden';
      element.style.height = 'auto';

      const height = getComputedStyle(element).height;

      element.style.width = null;
      element.style.position = null;
      element.style.visibility = null;
      element.style.height = 0;

      getComputedStyle(element).height;
      setTimeout(() => {
        element.style.height = height;
      });
    },
    afterEnter(element) {
      element.style.height = 'auto';
    },
    leave(element) {
      element.style.height = getComputedStyle(element).height;
      getComputedStyle(element).height;

      setTimeout(() => {
        element.style.height = 0;
      });
    },
    saveReplay(item) {
      if (this.replyProcessing || !this.replyText) {
        return;
      }

      this.replyProcessing = true;

      this.$apollo
        .mutate({
          mutation: replyMemberReviewMutation,
          variables: {
            id: item.id,
            text: this.replyText,
          },
        })
        .then(() => {
          this.replyingReviewId = null;
          this.replyText = '';
          this.$emit('reply-success');
        })
        .catch((err) => {
          this.$toast.error(err?.graphQLErrors?.[0]?.message);
        })
        .finally(() => {
          this.replyProcessing = false;
        });
    },
    close() {
      this.replyingReviewId = null;
      this.replyText = '';
    },
    showReply(id) {
      if (this.repliedReviewId) {
        this.repliedReviewId = null;
      } else {
        this.repliedReviewId = String(id);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/assets/scss/variables-site/variables' as vars;

.app-member-reviews-list {
  display: flex;
  flex-direction: column;

  .expand-enter-active,
  .expand-leave-active {
    transition: height 0.5s ease-in-out;
    overflow: hidden;
  }

  .expand-enter,
  .expand-leave-to {
    height: 0;
  }

  > .app-member-reviews-list-item:not(:last-child) {
    @media (max-width: vars.$tablet-break-point) {
      border-bottom: 1px solid vars.$c-gray-d8;
      margin-bottom: 15px;
      padding-bottom: 15px;
    }
  }
}

.app-member-reviews-list .child {
  margin-top: 48px;

  @media (max-width: vars.$tablet-break-point) {
    display: none;
    margin-top: 25px;

    &.child--show {
      display: flex;
    }
  }
}

.replies-action {
  width: max-content;
  margin-top: 12px;
  padding-left: 0;
  color: vars.$c-dark-gray;
  font-size: 16px;
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;
}

.review-list-item {
  &:not(:last-child) {
    padding-bottom: 36px;
    margin-bottom: 36px;
    border-bottom: 1px solid vars.$c-gray-d8;
  }
}

.reply {
  color: vars.$c-dark-gray;
  font-size: 16px;
  font-weight: 500;
  text-transform: uppercase;

  @media (min-width: vars.$tablet-break-point + 1) {
    font-size: 18px;
  }
}

.text-area {
  margin: 45px 0;
  width: 100%;
  justify-content: flex-end;
  position: relative;
  display: flex;
  transition: all 0.25s ease-in;

  textarea {
    width: 100%;
    height: 300px;
    padding-top: 23px;
    padding-left: 46px;
    padding-right: 70px;
    border: 1px solid vars.$c-gray-d8;
    line-height: 1.5;
    font-size: 16px;

    @media (min-width: vars.$tablet-break-point + 1) {
      font-size: 18px;
    }

    &::placeholder {
      color: vars.$c-gray-d8;
      font-size: 16px;
      font-weight: 400;

      @media (min-width: vars.$tablet-break-point + 1) {
        font-size: 18px;
      }
    }
  }

  .close-btn {
    position: absolute;
    top: 30px;
    right: 30px;
  }

  .save {
    color: vars.$c-dark-gray;
    font-size: 20px;
    font-weight: 700;
    letter-spacing: 3px;
    text-transform: uppercase;
    position: absolute;
    bottom: 30px;
    right: 30px;

    &:hover {
      opacity: 0.7;
    }
  }
}
</style>
